import { canonicalSubmission, sha256Hex, type SubmissionContent } from "../domain/canonical";
import { submissionRequestSchema, type SubmissionRequest } from "../domain/submission";
import type { Draft, Receipt } from "../domain/types";
import { deleteOutbox, listOutbox, saveOutbox, saveReceipt, type OutboxItem } from "./idb";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
    readonly retryable = false,
  ) {
    super(message);
  }
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs = 18_000): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal, credentials: "same-origin" });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("The connection timed out. Your answers remain on this device.", undefined, true);
    }
    throw new ApiError("Could not reach pitch.dog. Your answers remain on this device.", undefined, true);
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function makeSubmissionRequest(content: SubmissionContent): Promise<SubmissionRequest> {
  const payloadHash = await sha256Hex(canonicalSubmission(content));
  return submissionRequestSchema.parse({ content, payloadHash, consent: true, website: "" });
}

export async function submitRequest(request: SubmissionRequest): Promise<Receipt> {
  const response = await fetchWithTimeout("/api/submissions", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request),
  });
  const body = (await response.json().catch(() => null)) as { error?: string; receipt?: Receipt } | null;
  if (!response.ok || !body?.receipt) {
    throw new ApiError(body?.error ?? "Submission could not be protected.", response.status, response.status >= 500 || response.status === 429);
  }
  if (body.receipt.status !== "protected") throw new ApiError("Server did not return a protected receipt.", 502, true);
  await saveReceipt(body.receipt);
  return body.receipt;
}

export async function submitOrQueue(request: SubmissionRequest): Promise<Receipt> {
  const now = new Date().toISOString();
  const item: OutboxItem = {
    id: `${request.content.submissionId}:${request.content.revision}`,
    request,
    attempts: 0,
    createdAt: now,
    updatedAt: now,
  };
  await saveOutbox(item);
  try {
    const receipt = await submitRequest(request);
    await deleteOutbox(item.id);
    return receipt;
  } catch (error) {
    const apiError = error instanceof ApiError ? error : new ApiError("Submission failed.", undefined, true);
    await saveOutbox({ ...item, attempts: 1, updatedAt: new Date().toISOString(), lastError: apiError.message });
    throw apiError;
  }
}

export async function retryOutbox(): Promise<Receipt[]> {
  const items = await listOutbox();
  const receipts: Receipt[] = [];
  for (const item of items) {
    try {
      const receipt = await submitRequest(item.request);
      await deleteOutbox(item.id);
      receipts.push(receipt);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Retry failed";
      await saveOutbox({ ...item, attempts: item.attempts + 1, updatedAt: new Date().toISOString(), lastError: message });
    }
  }
  return receipts;
}

export async function startPrivateSession(resumeToken: string): Promise<Draft | null> {
  const response = await fetchWithTimeout("/api/drafts/session", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ resumeToken }),
  });
  const body = (await response.json().catch(() => null)) as { draft?: Draft | null; error?: string } | null;
  if (!response.ok) throw new ApiError(body?.error ?? "Private resume could not start.", response.status, response.status >= 500);
  return body?.draft ?? null;
}

export async function savePrivateDraft(draft: Draft): Promise<void> {
  const { resumeToken: _resumeToken, ...serverDraft } = draft;
  const response = await fetchWithTimeout("/api/drafts", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ draft: serverDraft }),
  }, 10_000);
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new ApiError(body?.error ?? "Private copy could not be updated.", response.status, response.status >= 500);
  }
}
